import {Inject, Injectable} from '@angular/core';
import {CalculationDataService} from './calculation-data.service';
import {PolySizeService} from './poly-size.service';
import {Geometry, Mesh, Vector2, Vector3} from 'three';
import {ContainerService} from '../../Utils/container.service';
import {ColorCodingService} from '../Painting/color-coding.service';
import {MarkingType} from '../Painting/markingType';
import {WebWorkerService} from 'ngx-web-worker';


@Injectable({
    providedIn: 'root'
})
export class TextureSizeService {
    private readonly _faceSet;

    // getter and setter
    private _pixelSet: Map<number, MarkingType>;
    private _area: number;
    private _change = false;
    public _modelWeight = 48;

    private _faceMap: any[];

    constructor(private calculationDataService: CalculationDataService,
                private polySizeService: PolySizeService,
                private colorCodingService: ColorCodingService,
                private containerService: ContainerService,
                @Inject(WebWorkerService) private _webWorkerService: WebWorkerService) {
        this._pixelSet = new Map();
        this._faceSet = new Set();
        this.area = 0;
    }


    get pixelSet(): Map<number, MarkingType> {
        return this._pixelSet;
    }

    set pixelSet(value: Map<number, MarkingType>) {
        this._pixelSet = value;
    }


    get faceSet(): any {
        return this._faceSet;
    }

    set faceSet(value: any) {
        this._pixelSet = value;
    }


    get area(): number {
        return this._area;
    }

    set area(value: number) {
        this._area = value;
    }


    get change(): boolean {
        return this._change;
    }

    set change(value: boolean) {
        this._change = value;
    }

    get faceMap(): ReadonlyArray<any> {
        return this._faceMap;
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    public calculateSurfaceProperties() {
        if (this.change) {
            const modelMesh = <Mesh>this.containerService.renderGroup.children[0];
            const surfaceProperties = this.calculateSurfacePropertiesForMesh(modelMesh);
            this.change = false;
            return surfaceProperties;
        }
        return null;
    }

    // TODO mwimmer: check if this calculation is correct
    public mapTexelToFace(mesh: Mesh, canvas: any) {
        this._faceMap = [];
        const geo = <Geometry>mesh.geometry;
        const uvs = geo.faceVertexUvs[0];
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';

        for (let j = 0; j < uvs.length; j++) {
            const i = Number(j);
            const face = geo.faces[i];

            const texelArray = [];
            if (face !== undefined) {
                const {pixCoordA, pixCoordB, pixCoordC} = this.getPixelCoordinates(uvs[i], canvas.width);
                const squareStart = new Vector2(Math.min(pixCoordA.x, pixCoordB.x, pixCoordC.x),
                                                Math.min(pixCoordA.y, pixCoordB.y, pixCoordC.y));
                const squareEnd = new Vector2(Math.max(pixCoordA.x, pixCoordB.x, pixCoordC.x),
                                              Math.max(pixCoordA.y, pixCoordB.y, pixCoordC.y));

                squareStart.x = Math.floor(squareStart.x) - 1;
                squareStart.y = Math.floor(squareStart.y) - 1;
                squareEnd.x = Math.ceil(squareEnd.x) + 1;
                squareEnd.y = Math.ceil(squareEnd.y) + 1;

                // Optical and faster solution
                ctx.beginPath();
                ctx.moveTo(pixCoordA.x, pixCoordA.y);
                ctx.lineTo(pixCoordB.x, pixCoordB.y);
                ctx.lineTo(pixCoordC.x, pixCoordC.y);
                ctx.fill();

                // TODO first step of improvement store only one one Map with all information (should save 50 of the memory)
                // TODO index is polygon and array contains all information
                const squareWidth  = squareEnd.x - squareStart.x;
                const squareHeight = squareEnd.y - squareStart.y;
                const value = ctx.getImageData(squareStart.x, squareStart.y, squareWidth, squareHeight).data;
                for (let q = 0; q < value.length; q += 4) {
                    const color = value[q] + value[q + 1] + value[q + 2];
                    if (color !== 0) {
                        const pixArea = color / 765;
                        const x = (q / 4) % squareWidth;
                        const y = Math.floor((q / 4) / squareWidth);
                        const texel = this.convertIndex(canvas.width, squareStart.x + x, squareStart.y + y);
                        texelArray.push({index: texel, area: pixArea});
                    }
                }
                ctx.clearRect(squareStart.x, squareStart.y, squareWidth, squareHeight);
                this._faceMap.push(texelArray);
            }
        }
    }

    public calculateTextureArea() {
        this.calculationDataService.burnareaT = 100;
    }

    public reset() {
        this._pixelSet = new Map<number, MarkingType>();
        this.calculationDataService.clear();
    }

    public setMarkedID(faceID: number) {
        this._faceSet.add(faceID);
    }

    public deleteMarkedID(faceID: number) {
        this._faceSet.delete(faceID);
    }

    public initColors(geo: Geometry) {
        this.colorCodingService.initColors(geo);
    }

    private convertIndex(textureWidth: number, x: number, y: number): number {
        return ((textureWidth * y + x) * 4);
    }

    public initArea(model: Mesh) {
        this.polySizeService.initPolyArea(model);
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    private calculateSurfacePropertiesForMesh(mesh: Mesh): any {
        console.log('calculate');
        const burnAreas = [0, 0, 0, 0];
        let polyBurned = 0;

        const geo = <THREE.Geometry>mesh.geometry;
        const faces = this.getFacesWithNeighbours();
        for (const j of faces) {
            const i = Number(j);
            const face = geo.faces[i];

            if (face) {
                const triangleArea = this.polySizeService.getFaceSize(face, geo);
                polyBurned += triangleArea;
                const burnedPixels = [0, 0, 0, 0];
                let facePix = 0;

                const faceMapping = this.faceMap[i];
                faceMapping.forEach(pixelMapping => {
                    facePix += pixelMapping.area;
                    const pixel = this._pixelSet.get(pixelMapping.index);
                    this.updateBurnedPixels(pixel, burnedPixels, pixelMapping);

                });

                if (facePix > 0) {
                    for (let a = 0; a < burnAreas.length; a++) {
                        burnAreas[a] += (burnedPixels[a] / facePix) * triangleArea;
                    }
                }
            }


        }

        let burnedTotal = burnAreas.reduce((a, b) => a + b);
        burnedTotal = (burnedTotal * 100) / this.polySizeService.polyArea;
        return {
            burnareaT:   this.calculationDataService.burnareaT   = burnedTotal,
            burnarea2aT: this.calculationDataService.burnarea2aT = (burnAreas[0] * 100) / this.polySizeService.polyArea,
            burnarea2bT: this.calculationDataService.burnarea2bT = (burnAreas[1] * 100) / this.polySizeService.polyArea,
            burnarea3T:  this.calculationDataService.burnarea3T  = (burnAreas[2] * 100) / this.polySizeService.polyArea,
            burnarea4T:  this.calculationDataService.burnarea4T  = (burnAreas[3] * 100) / this.polySizeService.polyArea,
            parkland:    this.calculationDataService.parkland    = 4 * this._modelWeight * burnedTotal
        };

    }

    /**
     *
     * @param uv
     * @param width: texture canvas width
     */
    private getPixelCoordinates(uv, width: number) {
        // get uv coordinates for triangle edges
        const texCoordA = new Vector3(uv[0].x, uv[0].y, 0);
        const texCoordB = new Vector3(uv[1].x, uv[1].y, 0);
        const texCoordC = new Vector3(uv[2].x, uv[2].y, 0);

        // calculate pixel coordinates from texture coordinates
        const pixCoordA = new Vector3(texCoordA.x * width, (1 - texCoordA.y) * width, 0);
        const pixCoordB = new Vector3(texCoordB.x * width, (1 - texCoordB.y) * width, 0);
        const pixCoordC = new Vector3(texCoordC.x * width, (1 - texCoordC.y) * width, 0);

        return {pixCoordA, pixCoordB, pixCoordC};
    }


    private updateBurnedPixels(pixel: MarkingType, burnedPixels: number[], pixelMapping) {
        if (pixel !== undefined) {
            if (pixel.color === this.colorCodingService.color2a) {
                // degree 2a
                burnedPixels[0] += pixelMapping.area;
            } else if (pixel.color === this.colorCodingService.color2b) {
                // degree 2b
                burnedPixels[1] += pixelMapping.area;
            } else if (pixel.color === this.colorCodingService.color3) {
                // degree 3
                burnedPixels[2] += pixelMapping.area;
            } else if (pixel.color === this.colorCodingService.color4) {
                // degree 4
                burnedPixels[3] += pixelMapping.area;
            }
        }
    }

    private getFacesWithNeighbours(): number[] {
        const faces = new Set<number>();
        console.log(this.polySizeService.faceSet);
        this.polySizeService.faceSet.forEach((i) => {
            faces.add(i);
            const neighbours = this.containerService.faceNeighbours.get(i);
            if (neighbours) {
                neighbours.forEach((j) => {
                    faces.add(j);
                });
            }
        });

        return Array.from(faces);
    }
}
